<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div
        v-if="showReconsent"
        class="fixed bottom-0 inset-x-0 z-9999 p-4 md:p-6"
        role="dialog"
        aria-label="Privacy policy update"
        aria-modal="false"
      >
        <div class="mx-auto max-w-3xl rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950 p-6 shadow-2xl">
          <div class="flex flex-col gap-4">
            <div class="flex items-start gap-3">
              <Icon name="tabler:alert-triangle" size="24" class="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div>
                <h2 class="text-lg font-semibold text-amber-900 dark:text-amber-100">
                  {{ $t('consent.reconsent.title') }}
                </h2>
                <p class="mt-1 text-sm text-amber-800 dark:text-amber-200">
                  {{ $t('consent.reconsent.description') }}
                </p>
              </div>
            </div>

            <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" size="sm" @click="handleWithdraw">
                {{ $t('consent.reconsent.withdraw') }}
              </Button>
              <Button variant="default" size="sm" @click="handleReviewAccept">
                {{ $t('consent.reconsent.reviewAccept') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from '#imports';
import Button from '#bubba/shared/components/ui/button/Button.vue';
import { useConsentState } from '#bubba/shared/composables/consent/useConsentState';
import { useConvexQuery, useConvex } from '#composables/useConvex';
import { api } from '@convex/_generated/api';
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('ReconsentBanner', { enabled: false });
const { $t } = useI18n();

const {
  state,
  acceptAll,
  resetConsent,
} = useConsentState();

// Query for stale consent types that need re-consent
const { data: staleTypes } = useConvexQuery(
  api.users.consents.getRequiredReconsents,
  {}
);

// Show banner when user has already interacted but has stale consents
const showReconsent = computed(() => {
  if (!state.value.hasInteracted) return false;
  if (!staleTypes.value || staleTypes.value.length === 0) return false;
  return true;
});

async function handleReviewAccept() {
  try {
    // Re-accept with the current policy version (the active one on the server)
    acceptAll();

    // Sync to server
    const { useConsentSync } = await import('#composables/useConsentSync');
    const { syncToServer } = useConsentSync();
    await syncToServer();

    log.info('User re-consented after policy update');
  } catch (e: unknown) {
    log.error('Failed to process re-consent', e);
  }
}

async function handleWithdraw() {
  try {
    // Reset all optional consents
    resetConsent();

    // Sync to server
    const { useConsentSync } = await import('#composables/useConsentSync');
    const { syncToServer } = useConsentSync();
    await syncToServer();

    log.info('User withdrew consent after policy update');
  } catch (e: unknown) {
    log.error('Failed to withdraw consent', e);
  }
}
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
